import * as THREE from 'three';
import type { Network, Connector } from '../data/types';
import { levelToHeight } from '../geometry/levels';

/**
 * Renders schematic internal station connections (passages / access shafts)
 * between levels. Geometry is a documented model assumption, not a surveyed
 * structure; `derived` connectors rest on OSM level evidence, `assumed` on the
 * documented model.
 */
export class ConnectionsLayer {
  readonly group = new THREE.Group();
  private items: { mesh: THREE.Mesh; line: THREE.Line; from: THREE.Vector3; to: THREE.Vector3; derived: boolean }[] = [];
  private tubeMat: THREE.MeshBasicMaterial;
  private derivedMat: THREE.MeshBasicMaterial;

  constructor() {
    this.group.name = 'connections';
    this.tubeMat = new THREE.MeshBasicMaterial({ color: 0xd68f3f, transparent: true, opacity: 0.75 });
    this.derivedMat = new THREE.MeshBasicMaterial({ color: 0x8ec74b, transparent: true, opacity: 0.8 });
  }

  build(network: Network) {
    for (const c of network.connectors) {
      this.addConnector(c);
    }
  }

  private addConnector(c: Connector) {
    const from = new THREE.Vector3(c.from.x, levelToHeight(c.from.level), c.from.z);
    const to = new THREE.Vector3(c.to.x, levelToHeight(c.to.level), c.to.z);
    if (from.distanceTo(to) < 0.5) return;

    const derived = c.quality === 'derived';
    const mesh = new THREE.Mesh(new THREE.TubeGeometry(curveBetween(from, to), 12, 2.2, 8, false), derived ? this.derivedMat : this.tubeMat);
    mesh.userData.isConnection = true;
    mesh.userData.connector = c;
    this.group.add(mesh);

    const line = new THREE.Line(
      new THREE.BufferGeometry().setFromPoints([from, to]),
      new THREE.LineBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.25 }),
    );
    this.group.add(line);
    this.items.push({ mesh, line, from, to, derived });
  }

  /** View-only transform mirroring the station level explosion. */
  setExplode(on: boolean) {
    const f = on ? 2.5 : 1;
    const scaleY = (y: number) => (Math.abs(y) > 0.01 ? y * f : y);
    for (const item of this.items) {
      const a = new THREE.Vector3(item.from.x, scaleY(item.from.y), item.from.z);
      const b = new THREE.Vector3(item.to.x, scaleY(item.to.y), item.to.z);
      item.mesh.geometry.dispose();
      item.mesh.geometry = new THREE.TubeGeometry(curveBetween(a, b), 12, 2.2, 8, false);
      item.line.geometry.dispose();
      item.line.geometry = new THREE.BufferGeometry().setFromPoints([a, b]);
    }
  }

  setVisible(v: boolean) {
    this.group.visible = v;
  }

  get pickTargets(): THREE.Object3D[] {
    return this.group.children.filter((o) => (o.userData as { isConnection?: boolean }).isConnection);
  }

  connectorFor(object: THREE.Object3D): Connector | null {
    const c = (object.userData as { connector?: Connector }).connector;
    return c ?? null;
  }
}

function curveBetween(from: THREE.Vector3, to: THREE.Vector3): THREE.CatmullRomCurve3 {
  const mid = from.clone().lerp(to, 0.5);
  return new THREE.CatmullRomCurve3([from, mid, to]);
}
